import React, { useEffect, useState } from "react";

import {
    Box,
    Paper,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    FormHelperText,
    Select,
    FormControl,
    InputLabel,
    MenuItem,
    styled,
    Card
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import { useNavigate } from "react-router-dom";
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';

import { useItems, itemTypes, targetTypes, uomConfig } from '../../../hooks/api/useItems';
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { initialValues, validationSchema } from "../../../schema/products/uomSchema";
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from "../../../hooks/useTranslations";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

const {
    decimalPlaces,
    metricTypes
} = uomConfig

export default function NewUom({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@product"] })

    const { promptMessage } = useSystemPrompt();
    const { isLoading, create } = useItems({ itemType: itemTypes.uom, targetType: targetTypes.uom });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.product.id, scopeTypes.product.uom.id)

    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const [selectedMetricType, setSelectedMetricType] = useState()
    const [metricDisplaySelection, setMetricDisplaySelection] = useState()

    useEffect(() => {

        const metricTypeId = Object.keys(metricTypes).filter(i => i === selectedMetricType)

        setMetricDisplaySelection(metricTypes[metricTypeId])

    }, [selectedMetricType])

    const onSubmit = async (currentValues) => {

        try {

            let data = {
                item_uom: {
                    name: currentValues?.name,
                    description: currentValues?.description,
                    metric_type: currentValues?.metricType,
                    metric_type_display_name: currentValues?.mTypeDisplay,
                    decimal_digit: currentValues?.deciPlaceDigit,
                    status: currentValues?.status ? 1 : 0,
                }
            }

            const response = await create({ data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("products/uoms"))

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        // enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const RenderForm = () => {
        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={4} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t('information')}
                                subTitle={t('Provide details of the profile')}
                            />

                        </Grid>
                        <Grid item xs={12} sm={12} md={8}>
                            <Grid container spacing={1} display={'flex'}>

                                <Grid item xs={12} md={6} textAlign="left">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="status"
                                                checked={Boolean(values.status)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("status", checked);
                                                }}
                                            />
                                        }
                                        label={
                                            Boolean(values.status) ? t('enabled') : t('disabled')
                                        }
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    {/* Space down */}
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"name"}
                                        label={t('uomName')}
                                        type="text"
                                        variant="outlined"
                                        value={values.name}
                                        onChange={handleChange}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    {/* Space down */}
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControl
                                        fullWidth
                                        margin="dense"
                                        size="small"
                                        error={touched.metricType && Boolean(errors.metricType)}
                                    >
                                        <InputLabel id="metricType">{t('metricType')}</InputLabel>
                                        <Select
                                            labelId="metricType"
                                            label={t('metricType')}
                                            name={"metricType"}
                                            defaultValue="volume"
                                            onChange={(event) => {
                                                const val = event.target.value;
                                                setFieldValue(`metricType`, val)
                                                setFieldValue(`mTypeDisplay`, "")
                                                setFieldValue(`deciPlaceDigit`, "")
                                                setSelectedMetricType(val)
                                            }}
                                            value={values.metricType}
                                            sx={{
                                                textAlign: 'left'
                                            }}
                                        >
                                            {
                                                Object.keys(metricTypes).map((i, index) => {
                                                    return (
                                                        <MenuItem
                                                            key={index}
                                                            value={i}
                                                        >{i}</MenuItem>
                                                    )
                                                })
                                            }

                                        </Select>
                                        <FormHelperText>
                                            {touched.metricType && errors.metricType}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={touched.mTypeDisplay && Boolean(errors.mTypeDisplay)}
                                    >
                                        <InputLabel id="mTypeDisplay">{t('metricTypeDisplayName')}</InputLabel>
                                        <Select
                                            labelId="mTypeDisplay"
                                            label={t('metricTypeDisplayName')}
                                            name={"mTypeDisplay"}
                                            onChange={handleChange}
                                            value={values.mTypeDisplay}
                                            sx={{
                                                textAlign: 'left'
                                            }}
                                        >
                                            {
                                                metricDisplaySelection?.map((i, index) => {
                                                    return (
                                                        <MenuItem
                                                            key={index}
                                                            value={i.id}
                                                        >{i.name} ({i.abbreviation})</MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {touched.mTypeDisplay && errors.mTypeDisplay}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"description"}
                                        label={t('description')}
                                        type="text"
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={6} >
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                        error={touched.deciPlaceDigit && Boolean(errors.deciPlaceDigit)}
                                    >
                                        <InputLabel id="deciPlaceDigit">{t('decimalPlacesDigit')}</InputLabel>
                                        <Select
                                            labelId="deciPlaceDigit"
                                            label={t('decimalPlacesDigit')}
                                            name={"deciPlaceDigit"}
                                            onChange={handleChange}
                                            value={values.deciPlaceDigit}
                                            sx={{
                                                textAlign: 'left'
                                            }}
                                        >

                                            {
                                                decimalPlaces.map((i, index) => {
                                                    return (
                                                        <MenuItem
                                                            key={index}
                                                            value={i}
                                                        >{i}</MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {touched.deciPlaceDigit && errors.deciPlaceDigit}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.newUom })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('newUom')}
                breadcrumbs={[
                    {
                        displayText: t('uoms'),
                        props: {
                            href: getRoute("products/uoms")
                        }
                    },
                    {
                        displayText: t('newUom'),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageNew.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}